import { Capacitor } from '@capacitor/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { isNative } from '@/domains/shared/utils/native';

/**
 * Turns Crashlytics collection on at runtime.
 *
 * Both native projects ship with automatic collection disabled
 * (`FirebaseCrashlyticsCollectionEnabled` on iOS,
 * `firebase_crashlytics_collection_enabled` on Android), so in a Release build
 * this call is what actually starts crash reporting.
 *
 * This JavaScript is delivered over the air by live updates, so it can land
 * on a native binary built before the Crashlytics plugin existed. There the
 * bridge has no `FirebaseCrashlytics` registered, so `isPluginAvailable` also
 * doubles as the rollout gate — it only starts returning true once the
 * native build ships the plugin.
 */
export const enableCrashlytics = async (): Promise<void> => {
  // The web implementation throws `unimplemented` for every method, and
  // isPluginAvailable returns true there because that stub is registered.
  if (!isNative()) return;

  if (!Capacitor.isPluginAvailable('FirebaseCrashlytics')) {
    console.info(
      'Crashlytics plugin not present in this native build — skipping enable',
    );
    return;
  }

  try {
    await FirebaseCrashlytics.setEnabled({ enabled: true });
  } catch (error) {
    // Enabling crash reporting must never be the thing that breaks app start-up.
    console.warn('Crashlytics setEnabled failed', error);
  }
};
